import React, { useState } from "react";
import { Helmet } from "react-helmet";

import Navbar from "../components/navbar";
import HeaderSeparator from "../components/HeaderSeparator";
import Footer from "../components/footer";

import "../assets/styles/donateus.css";

import bankImage from "../assets/images/donations/bankQR.jpg";

const DonateUs = () => {
  const [copied, setCopied] = useState(false);

  const absoluteDonateUsImageUrl =
    "https://www.kalpabriksha.org/images/donate-share-image.jpg";

  const accountNumber = "00401040251617";

  /*
   * Approximate rate used only to show donors the equivalent NPR amount.
   * Update this one value whenever necessary.
   */
  const USD_TO_NPR = 150;

  const formatNPR = (usd) =>
    Math.round(usd * USD_TO_NPR).toLocaleString("en-US");

  const copyAccountNumber = async () => {
    try {
      await navigator.clipboard.writeText(accountNumber);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Unable to copy account number:", error);
    }
  };

  const impactExamples = [
    {
      usd: 1,
      icon: "fas fa-pencil-alt",
      title: "Every contribution counts",
      description:
        "Helps with small but essential costs such as stationery, printing, and materials used during our programs.",
    },
    {
      usd: 5,
      icon: "fas fa-book-open",
      title: "Support learning materials",
      description:
        "Can help provide printed resources, activity sheets, awareness materials, and supplies for youth sessions.",
    },
    {
      usd: 10,
      icon: "fas fa-hands-helping",
      title: "Support our volunteers",
      description:
        "Can contribute toward local travel, meals, and field expenses for volunteers working in communities.",
    },
    {
      usd: 25,
      icon: "fas fa-bullhorn",
      title: "Help run an outreach session",
      description:
        "Can support materials and logistical expenses for school and community-based outreach activities.",
    },
    {
      usd: 50,
      suffix: "+",
      icon: "fas fa-users",
      title: "Strengthen a larger program",
      description:
        "Can make a meaningful contribution toward workshops, training programs, and larger youth-led initiatives.",
    },
  ];

  const donationUses = [
    {
      icon: "fas fa-users",
      title: "Youth Programs",
      description:
        "School outreach, mentorship initiatives, workshops, open mics, and other youth-led programs.",
    },
    {
      icon: "fas fa-book-open",
      title: "Learning Materials",
      description:
        "Modules, toolkits, activity sheets, brochures, magazines, and educational resources.",
    },
    {
      icon: "fas fa-hands-helping",
      title: "Volunteer & Field Support",
      description:
        "Reasonable travel, meals, accommodation, and field expenses for volunteers, trainers, and mentors.",
    },
    {
      icon: "fas fa-award",
      title: "Participation & Recognition",
      description:
        "Certificates, prizes, educational materials, and recognition for participating youth.",
    },
    {
      icon: "fas fa-lightbulb",
      title: "Research & Awareness",
      description:
        "Research reports, public-awareness materials, printing, publication, and distribution.",
    },
    {
      icon: "fas fa-laptop",
      title: "Digital Access",
      description:
        "Our website, communication platforms, digital resources, and tools that make our work accessible.",
    },
    {
      icon: "fas fa-cogs",
      title: "Essential Operations",
      description:
        "A limited portion may support communication, coordination, office supplies, and necessary administration.",
    },
  ];

  return (
    <div className="donate-page">
      <Helmet>
        <title>Donate | Kalpabriksha Nepal</title>

        <meta
          name="description"
          content="Support Kalpabriksha Nepal and help us expand youth empowerment, education, mentorship, awareness, and community outreach programs."
        />

        <meta
          name="keywords"
          content="Donate, Kalpabriksha Nepal, NGO Nepal, youth empowerment Nepal, Change Because We Can, education, community outreach"
        />

        <meta
          property="og:title"
          content="Donate to Kalpabriksha Nepal | Support Youth-Led Change"
        />

        <meta
          property="og:description"
          content="Your contribution helps Kalpabriksha Nepal organize youth programs, educational campaigns, mentorship initiatives, and community outreach."
        />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://kalpabriksha.org/donate" />
        <meta property="og:image" content={absoluteDonateUsImageUrl} />
      </Helmet>

      <Navbar />

      <HeaderSeparator
        title="Donate"
        breadcrumb={[
          { name: "Home", link: "/" },
          { name: "Donate" },
        ]}
      />

      <main className="donate-main">
        {/* ================= INTRO ================= */}

        <section className="donate-section donate-intro-section">
          <div className="donate-container">
            <div className="donate-intro">
              <span className="donate-section-label">SUPPORT OUR WORK</span>

              <h2>Help us create more opportunities for young people.</h2>

              <p>
                Your support helps Kalpabriksha take meaningful programs into
                schools and communities, develop educational resources, support
                volunteers, and create spaces where young people can learn,
                participate, and lead.
              </p>

              <div className="intro-points">
                <div className="intro-point">
                  <i className="fas fa-users"></i>

                  <div>
                    <strong>Support Youth Programs</strong>
                    <span>Workshops, outreach and mentorship</span>
                  </div>
                </div>

                <div className="intro-point">
                  <i className="fas fa-book-open"></i>

                  <div>
                    <strong>Provide Resources</strong>
                    <span>Education and awareness materials</span>
                  </div>
                </div>

                <div className="intro-point">
                  <i className="fas fa-hands-helping"></i>

                  <div>
                    <strong>Strengthen Communities</strong>
                    <span>Youth participation and opportunity</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= BANK ================= */}

        <section id="bank-transfer" className="donate-section donate-bank-section">
          
          <div className="donate-container">
            <div className="donate-section-heading">
              <span className="donate-section-label">
                MAKE A CONTRIBUTION
              </span>

              <h2>Donate directly to Kalpabriksha</h2>

              <p>
                Contributions can currently be made directly to our
                organizational bank account.
              </p>
            </div>

            <div className="bank-card">
              <div className="bank-card-header">
                <div className="bank-icon">
                  <img src={bankImage} alt="Bank Transfer" />
                </div>

                <div>
                  <span>Bank Transfer</span>
                  <h3>Kalpabriksha Nepal</h3>
                </div>
              </div>

              <div className="bank-details">
                <div className="bank-detail-row">
                  <span>Account Name</span>
                  <strong>Kalpabriksha Nepal</strong>
                </div>

                <div className="bank-detail-row">
                  <span>Bank</span>
                  <strong>Nepal Investment Mega Bank</strong>
                </div>

                <div className="bank-detail-row">
                  <span>Branch</span>
                  <strong>Pulchok Branch</strong>
                </div>

                <div className="bank-detail-row account-number-row">
                  <span>Account Number</span>

                  <div className="account-number">
                    <strong>{accountNumber}</strong>

                    <button
                      type="button"
                      onClick={copyAccountNumber}
                      className={copied ? "copy-button copied" : "copy-button"}
                    >
                      <i
                        className={
                          copied
                            ? "fas fa-check"
                            : "far fa-copy"
                        }
                      ></i>

                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= IMPACT ================= */}

        <section className="donate-section impact-section">
          <div className="donate-container">
            <div className="donate-section-heading centered">
              <span className="donate-section-label">YOUR IMPACT</span>

              <h2>What can your donation help provide?</h2>

              <p>
                Every contribution becomes part of the resources that keep our
                programs running. These examples show how contributions of
                different sizes can support our work.
              </p>
            </div>

            <div className="impact-grid">
              {impactExamples.map((impact) => (
                <div className="impact-card" key={impact.usd}>
                  <div className="impact-icon">
                    <i className={impact.icon}></i>
                  </div>

                  <div className="impact-amount">
                    ${impact.usd}
                    {impact.suffix || ""}
                  </div>

                  <div className="impact-npr">
                    ≈ NPR {formatNPR(impact.usd)}
                    {impact.suffix || ""}
                  </div>

                  <h3>{impact.title}</h3>

                  <p>{impact.description}</p>
                </div>
              ))}
            </div>

            <div className="exchange-note">
              <i className="fas fa-info-circle"></i>

              <span>
                NPR equivalents are approximate and calculated at{" "}
                <strong>
                  USD 1 ≈ NPR {USD_TO_NPR}
                </strong>
                . Actual exchange rates may vary.
              </span>
            </div>
          </div>
        </section>

        {/* ================= USE OF DONATIONS ================= */}

        <section className="donate-section donation-use-section">
          <div className="donate-container">
            <div className="donate-section-heading centered">
              <span className="donate-section-label">
                HOW DONATIONS SUPPORT KALPABRIKSHA
              </span>

              <h2>Your contribution helps turn ideas into action.</h2>

              <p>
                Donations help meet the practical costs of reaching young
                people, developing resources, supporting our teams, and
                sustaining our programs.
              </p>
            </div>

            <div className="donation-use-grid">
              {donationUses.map((item) => (
                <div className="donation-use-card" key={item.title}>
                  <div className="use-icon">
                    <i className={item.icon}></i>
                  </div>

                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TRANSPARENCY ================= */}

        <section className="donate-section transparency-section">
          <div className="donate-container">
            <div className="transparency-card">
              <div className="transparency-icon">
                <i className="fas fa-shield-alt"></i>
              </div>

              <div>
                <span className="donate-section-label">
                  TRANSPARENCY MATTERS
                </span>

                <h2>Your trust matters to us.</h2>

                <p>
                  Kalpabriksha believes donations should be used responsibly,
                  transparently, and in ways that strengthen our mission. We
                  maintain records of organizational income and expenditure
                  and follow our financial and organizational procedures when
                  using donated funds.
                </p>

                <p>
                  A limited portion of contributions may support essential
                  administrative and operational expenses necessary to keep
                  our programs functioning effectively.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}

        <section className="donate-final-cta">
          <div className="donate-container">
            <span>BE PART OF THE CHANGE</span>

            <h2>
              Small contributions can create meaningful opportunities.
            </h2>

            <p>
              Every contribution helps us continue our programs and reach more young
  people. Your support becomes part of the work we do with young people and communities.
            </p>

            <a href="#bank-transfer" className="donate-cta-button">
              SUPPORT KALPABRIKSHA
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DonateUs;